import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import {
  actionColumn,
  type Column,
  DataGrid,
  editableColumn,
  Page,
  QueryContainer,
  SelectField,
} from '../../components';
import {
  CancelButton,
  EditButton,
  SaveButton,
} from '../../components/controls/buttons';
import { useStore } from '../../store';
import type Setting from './store/Setting';

const columns: Column<Setting>[] = [
  { header: 'Options', id: 'title' },
  editableColumn({
    header: 'Value',
    id: 'value',

    format(model: Setting) {
      switch (model.type) {
        case 'select': {
          return (
            model.options.find((o) => o.value === model.value)?.label ||
            model.value
          );
        }
        default: {
          return model.value;
        }
      }
    },

    renderEditor(model: Setting) {
      switch (model.type) {
        case 'select': {
          return (
            <SelectField
              name="value"
              onChange={model.handleChange}
              options={model.options}
              value={model.value}
            />
          );
        }
        default: {
          return '';
        }
      }
    },
  }),
  actionColumn({
    renderContent(model) {
      return (
        <>
          <EditItemButton model={model} />
          <SaveItemButton model={model} />
          <CancelItemEditButton model={model} />
        </>
      );
    },
  }),
];

export default observer(function SettingsListPage() {
  const { settingStore } = useStore();
  const [query] = useState(() => settingStore.createQuery());

  return (
    <Page>
      <QueryContainer noPagination query={query}>
        {(data) => <DataGrid<Setting> columns={columns} data={data} />}
      </QueryContainer>
    </Page>
  );
});

const EditItemButton = observer(function EditItemButton({
  model,
}: {
  model: Setting;
}) {
  return model.isEditing ? null : (
    <EditButton onClick={() => model.startEdit()} />
  );
});

const SaveItemButton = observer(function SaveItemButton({
  model,
}: {
  model: Setting;
}) {
  const { settingStore } = useStore();

  async function onClick() {
    await settingStore.update(model);
    model.commitEdit();
  }

  return model.isEditing ? <SaveButton onClick={onClick} /> : null;
});

const CancelItemEditButton = observer(function CancelItemEditButton({
  model,
}: {
  model: Setting;
}) {
  return model.isEditing ? (
    <CancelButton onClick={() => model.rejectEdit()} />
  ) : null;
});
